import i18n from '@/utils/i18n';
import PriceArrowDown from '@assets/icons/price-arrow-down.svg';
import PriceArrowUp from '@assets/icons/price-arrow-up.svg';
import React, { ReactElement, useMemo, useRef } from 'react';
import { ChatStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import { getAddedVariantPackEventPayloads } from '@/utils/ads';
import { INPUT_HEIGHT, OverrideInput } from '@/components/override-input';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { PortalHost } from '@gorhom/portal';
import { PriceEditDisplayInfoDetail, PriceEditDisplayInfoTrend } from '@/types';
import { PriceOverrideInput } from '@/components/price-override-input';
import { PriceTrendsSalesRepTile } from './price-trends-sales-rep-tile';
import { QuantitySelector } from '@/components/quantity-selector';
import {
  SalesRepInfoCell,
  SalesRepInfoCellText,
} from '@/components/orders/sales-rep-info-cell';
import { SalesRepPurchaseHistoryInfo } from './sales-rep-purchase-history-info';
import { SalesRepTile } from './sales-rep-tile';
import { SalesRepVariantPackDetails } from './sales-rep-variant-pack-details';
import { sharedStyles } from '@/components/orders/item-detail-actions';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { TEXT_INPUT_HEIGHT } from '@/components/text-input';
import {
  UpdateQuantityData,
  UpdateQuantityFn,
} from '@/contexts/new-order-actions-context-types';
import {
  useAddToOrderDisabled,
  useAvailabilityStatusEnabled,
} from '@/hooks/use-add-to-order-disabled';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { usePaymentsContext } from '@/contexts/payments-context';
import { usePriceEditing } from '@/hooks/use-price-editing';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  itemMetadata: ItemListOptionMetadata;
  onSalesRepPriceUpdated?: () => void;
  updateQuantity?: UpdateQuantityFn;
}

const chunkArray = (
  arr: PriceEditDisplayInfoDetail[] | undefined,
  size: number,
) => {
  if (!arr) {
    return [];
  }
  const chunkedArr: PriceEditDisplayInfoDetail[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunkedArr.push(arr.slice(i, i + size));
  }
  return chunkedArr;
};

export const SalesRepDetails: React.FC<Props> = ({
  containerStyle,
  itemMetadata,
  onSalesRepPriceUpdated,
  updateQuantity,
}) => {
  const componentUUID = useRef(uuidv4());
  const [styles] = useStyles();
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );
  const { isSmallLayout } = useLayoutContext();
  const variantPackUUID = itemMetadata.variantPackUUID;
  const { currencyCode, translatedTitles } = useVariantPackInfoContext();
  const { currencyCode: supplierCurrencyCode } = usePaymentsContext();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const visibleQuantity = useItemVisibleQuantity(variantPackUUID);
  const isAddToOrderDisabled = useAddToOrderDisabled(variantPackUUID);
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();
  const {
    selectedRestaurantRelationship: { chatUUIDs, chatStatus },
  } = useUserContext();
  const isProspect = chatStatus === ChatStatus.PROSPECT;
  const isInOrderInboundsStack = useOrderInboundsStore(
    (state) => state.isInOrderInboundsStack,
  );
  const { bottom } = useSafeAreaInsets();

  const {
    // Data
    currentPriceInMicros,
    grossProfitInMicros,
    isUpdatingGrossProfit,
    isUpdatingPrice,
    onHandQuantity,
    vpDisplayConfigs,

    // Formatted display values
    priceDisplayValue,
    grossProfitDisplayValue,

    // input handlers
    setSalesRepPriceInMicros,
    setSalesRepGrossProfitPercent,
    clearEdits,

    // save callbacks
    createConfirmHandler,

    // settings
    isPriceEditingEnabled,
    salesRepGrossProfitEnabled,
    salesRepPricingEnabled,

    // price edit calculator
  } = usePriceEditing(variantPackUUID);

  const handleConfirm = useMemo(
    () =>
      createConfirmHandler({
        onSuccess: () => onSalesRepPriceUpdated?.(),
      }),
    [createConfirmHandler, onSalesRepPriceUpdated],
  );

  const isInputEditable =
    isPriceEditingEnabled && !isUpdatingPrice && !isUpdatingGrossProfit;

  const renderChildrenForConfig = (
    detail: PriceEditDisplayInfoDetail,
  ): ReactElement => {
    if (detail.currency !== null) {
      return (
        <View style={detail.trend !== null ? styles.salesCostContainer : null}>
          {detail.type === 'current_price' &&
          typeof currentPriceInMicros === 'number' ? (
            <SalesRepInfoCellText>
              {formatCurrency(currentPriceInMicros, currencyCode)}
            </SalesRepInfoCellText>
          ) : detail.type === 'gross_profit' &&
            typeof grossProfitInMicros === 'number' ? (
            <SalesRepInfoCellText>
              {formatCurrency(grossProfitInMicros, currencyCode)}
            </SalesRepInfoCellText>
          ) : (
            <View style={styles.salesCostContent}>
              <SalesRepInfoCellText>
                {formatCurrency(
                  Number(detail.value) * MICRO_CONVERSION,
                  currencyCode,
                )}
              </SalesRepInfoCellText>
              {detail.trend !== null ? (
                <View style={styles.salesCostIcon}>
                  {detail.trend === PriceEditDisplayInfoTrend.Up ? (
                    <PriceArrowUp />
                  ) : (
                    <PriceArrowDown />
                  )}
                </View>
              ) : null}
            </View>
          )}
        </View>
      );
    } else {
      return <SalesRepInfoCellText>{detail.value}</SalesRepInfoCellText>;
    }
  };

  const topRowContentMinHeight =
    salesRepPricingEnabled || salesRepGrossProfitEnabled ? INPUT_HEIGHT : null;

  const editableConfigs = (
    <>
      {/* PRICE */}
      <SalesRepInfoCell
        containerStyle={styles.resetMargin}
        contentMinHeight={topRowContentMinHeight}
        isEditable={isPriceEditingEnabled}
        label={i18n.t('price')}
        labelStyle={styles.label}
      >
        <PortalHost
          name={`DSRToolingScreen__Price__${variantPackUUID}__${componentUUID.current}`}
        />
        <PriceOverrideInput
          containerStyle={[
            styles.inputStyle,
            isSmallLayout
              ? styles.inputHeightMobile
              : styles.inputHeightDesktop,
          ]}
          currencyCode={currencyCode}
          editable={isInputEditable}
          focusable={isInputEditable}
          inputAccessoryViewID="price-override-input"
          isConfirming={isUpdatingPrice}
          onChange={setSalesRepPriceInMicros}
          onClear={clearEdits}
          onConfirm={handleConfirm}
          portalHost={`DSRToolingScreen__Price__${variantPackUUID}__${componentUUID.current}`}
          testID="price-override-input"
          value={priceDisplayValue}
        />
      </SalesRepInfoCell>

      {/* GROSS PROFIT % */}
      {showGrossProfit ? (
        <SalesRepInfoCell
          containerStyle={styles.resetMargin}
          contentMinHeight={topRowContentMinHeight}
          isEditable={isPriceEditingEnabled}
          label={i18n.t('gross_profit_percent')}
          labelStyle={styles.label}
        >
          <PortalHost
            name={`DSRToolingScreen__GrossProfitPercent__${variantPackUUID}__${componentUUID.current}`}
          />
          <OverrideInput
            allowNegative
            allowZero
            containerStyle={[
              styles.inputStyle,
              isSmallLayout
                ? styles.inputHeightMobile
                : styles.inputHeightDesktop,
            ]}
            editable={isInputEditable}
            focusable={isInputEditable}
            inputAccessoryViewID="gross-profit-override-input"
            isConfirming={isUpdatingGrossProfit}
            onChange={setSalesRepGrossProfitPercent}
            onClear={clearEdits}
            onConfirm={handleConfirm}
            portalHost={`DSRToolingScreen__GrossProfitPercent__${variantPackUUID}__${componentUUID.current}`}
            testID="gross-profit-override-input"
            value={grossProfitDisplayValue}
          />
        </SalesRepInfoCell>
      ) : null}
    </>
  );

  const NUMBER_OF_CELLS_PER_ROW = 3;

  const onHandConfig: PriceEditDisplayInfoDetail = {
    currency: null,
    title: 'on_hand', // Hard coded value that will map to one in VariantPackInfoContext translatedTitles
    type: 'metadata', // Type that's defined in Shishito for generic metadata
    value:
      typeof onHandQuantity === 'number' ? onHandQuantity.toString() : null,
  };
  const alreadyHasOnHand = vpDisplayConfigs?.find(
    (config) => config.type === 'on_hand',
  );

  const chunkedConfigs = chunkArray(
    alreadyHasOnHand
      ? vpDisplayConfigs
      : (vpDisplayConfigs || []).concat([onHandConfig]),
    NUMBER_OF_CELLS_PER_ROW,
  );

  const altText = [itemMetadata.displayName, itemMetadata.externalID]
    .filter(Boolean)
    .join('-');

  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{
        paddingBottom: isSmallLayout
          ? Math.max(bottom, SCREEN_PADDING)
          : undefined,
      }}
      keyboardShouldPersistTaps="handled"
      style={[styles.container, containerStyle]}
    >
      <View style={styles.listItemContainer}>
        <OrderableItemListItemImage
          alt={altText}
          hidePlaceholderImages={hidePlaceholderImages}
          imageURLs={itemMetadata?.photoURLs ?? []}
          style={styles.imagePreview}
        />
        {itemMetadata ? (
          <SalesRepVariantPackDetails
            availabilityStatus={itemMetadata.availabilityStatus}
            availableQuantity={itemMetadata.availableQuantity}
            badges={itemMetadata.badges}
            displayName={itemMetadata.displayName}
            externalID={itemMetadata.externalID}
            packUnit={itemMetadata.packUnit}
          />
        ) : null}
        {isAddToOrderDisabled ? (
          availabilityStatusEnabled ? null : (
            <View style={[sharedStyles.unavailable, styles.quantityWrapper]}>
              <Text style={TYPOGRAPHY.pSemiBold}>{i18n.t('unavailable')}</Text>
            </View>
          )
        ) : !isProspect && !isInOrderInboundsStack ? (
          <QuantitySelector
            canDecrementToZero
            isFocused={false}
            maxQuantity={itemMetadata.maximumQuantity}
            minQuantity={itemMetadata.minimumQuantity}
            onChange={(previousQuantity: number, newQuantity: number) => {
              const updateQuantityData: UpdateQuantityData = {
                eventPayloads: getAddedVariantPackEventPayloads(itemMetadata),
                newQuantity,
                previousQuantity,
                variantPackDetails: {
                  itemDisplayName: itemMetadata.displayName,
                  metadata: itemMetadata?.variantPackMetadata || null,
                },
                variantPackUUID,
              };
              // we call the passed in updateQuantity function if it exists
              // because for order summary list item, we need to handle the
              // case where the quantity is 0 and exit out of order summary sheet
              if (updateQuantity) {
                updateQuantity(updateQuantityData);
              } else {
                debouncedUpdateQuantity(updateQuantityData, {
                  source: 'sales-rep-details',
                });
              }
            }}
            quantity={visibleQuantity}
            // zeroQuantityBehavior={zeroQuantityBehavior}
          />
        ) : null}
      </View>
      <SalesRepTile title={i18n.t('details')}>
        <View
          style={[!isSmallLayout ? styles.overrideRow : undefined, styles.base]}
        >
          {editableConfigs}
        </View>
        {chunkedConfigs.map((chunk, rowIndex) => (
          <View key={rowIndex} style={styles.detailsRow}>
            {chunk.map((detail, index) => (
              <SalesRepInfoCell
                containerStyle={[styles.resetMargin, styles.nonEditableCell]}
                contentMinHeight={topRowContentMinHeight}
                key={index}
                label={translatedTitles[detail.title]}
                labelStyle={styles.labelSecondary}
              >
                {detail.value ? renderChildrenForConfig(detail) : '--'}
              </SalesRepInfoCell>
            ))}
            {/**
             * When the final row is two elements, we need to render and additional element
             * so that three columns are rendered, as opposed to two 50% width columns.
             */}
            {chunk.length === 2 ? (
              <View style={styles.salesRepInfoSpacer} />
            ) : null}
          </View>
        ))}
      </SalesRepTile>
      <SalesRepTile title={i18n.t('history')}>
        <SalesRepPurchaseHistoryInfo
          chatUUID={chatUUIDs[0]}
          currencyCode={currencyCode ?? supplierCurrencyCode}
          variantPackUUID={variantPackUUID}
        />
      </SalesRepTile>
      <PriceTrendsSalesRepTile
        chatUUID={chatUUIDs[0]}
        variantPackUUID={variantPackUUID}
      />
    </KeyboardAwareScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    position: 'relative',
  },
  container: {
    padding: 24,
  },
  detailsRow: {
    flexDirection: 'row',
  },
  imagePreview: {
    borderColor: theme.colors.imageBorder,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    height: 56,
    width: 56,
  },
  inputHeightDesktop: {
    height: 48,
  },
  inputHeightMobile: {
    height: TEXT_INPUT_HEIGHT,
  },
  inputStyle: {
    paddingLeft: 4, // we need to add a paddingLeft of 12, but since price has a left element of 8, we need to add 4 more
    width: '100%',
  },
  label: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    fontSize: 13,
  },
  labelSecondary: {
    color: theme.colors.inactiveText,
    fontSize: 13,
  },
  listItemContainer: {
    flexDirection: 'row',
    paddingBottom: 24,
  },
  nonEditableCell: {
    marginLeft: 8,
  },
  overrideRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 16,
  },
  quantityWrapper: {
    alignItems: 'flex-end',
  },
  resetMargin: {
    margin: 0,
    marginVertical: 12,
  },
  salesCostContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  salesCostContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  salesCostIcon: {
    marginLeft: 2,
  },
  salesRepInfoSpacer: {
    flex: 1,
  },
}));
